import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Compass, Upload, Sparkles, ClipboardCheck, Scale, Radar, LineChart, FileText,
  Grid3x3, Gauge, Users2, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { nextActionsApi, NextAction, ActionTone } from '@/lib/nextActions';

const ICONS: Record<string, typeof Upload> = {
  upload: Upload,
  sparkles: Sparkles,
  'clipboard-check': ClipboardCheck,
  scale: Scale,
  radar: Radar,
  'line-chart': LineChart,
  'file-text': FileText,
  grid: Grid3x3,
  gauge: Gauge,
  users: Users2,
};

const TONE: Record<ActionTone, { dot: string; chip: string; label: string }> = {
  urgent: { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700', label: 'Do now' },
  recommended: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700', label: 'Recommended' },
  suggested: { dot: 'bg-ink-300', chip: 'bg-ink-100 text-ink-600', label: 'Suggested' },
};

function hrefFor(wsId: string, a: NextAction): string {
  const base = `/app/workspace/${wsId}`;
  return a.path.startsWith('?') ? `${base}${a.path}` : `${base}/${a.path}`;
}

/**
 * "Next steps" — the guidance hub on the dashboard (NUMBER_ONE_PLAN Wave U2).
 * Always tells the user the highest-value next action given their real data:
 * import, review approvals/anomalies/variances, build a forecast, etc. Every
 * row deep-links to the exact page so the tools stop feeling disconnected.
 * Distinct from ActivationChecklist (first-run setup); this is ongoing.
 */
export function NextStepsCard({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['next-actions', wsId],
    queryFn: () => nextActionsApi.get(wsId),
    enabled: !!wsId,
    staleTime: 60_000,
    retry: false,
  });

  if (!data || data.actions.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Compass className="h-4 w-4 text-emerald-700" />
        <h2 className="text-sm font-semibold text-ink-900">{t('next.title', { defaultValue: 'What to do next' })}</h2>
        {data.allCaughtUp && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t('next.caught_up', { defaultValue: "You're all caught up" })}
          </span>
        )}
      </div>

      <ul className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        {data.actions.map((a) => {
          const Icon = ICONS[a.icon] ?? Sparkles;
          const tone = TONE[a.tone];
          return (
            <li key={a.key}>
              <Link
                to={hrefFor(wsId, a)}
                className="group flex h-full items-start gap-3 rounded-lg border border-border bg-background/40 p-3 hover:border-emerald-300 hover:bg-white transition-colors"
              >
                <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-ink-50 text-ink-600 group-hover:text-emerald-700">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] font-semibold text-ink-900 truncate">
                      {t(`next.${a.key}.title`, { defaultValue: a.title })}
                    </span>
                    {typeof a.count === 'number' && a.count > 0 && (
                      <span className="num inline-flex items-center justify-center rounded-full bg-ink-900 px-1.5 text-[10px] font-semibold text-white">{a.count}</span>
                    )}
                    <span className={`ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide shrink-0 ${tone.chip}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                      {t(`next.tone.${a.tone}`, { defaultValue: tone.label })}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground line-clamp-2">
                    {t(`next.${a.key}.rationale`, { defaultValue: a.rationale })}
                  </p>
                  <span className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700">
                    {t(`next.${a.key}.cta`, { defaultValue: a.cta })}
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
